import { useSessionStore } from "@auth/sessionStore";
import {
  clinicBrandingClient,
  type ClinicBranding,
} from "@clinic/clinicBrandingClient";
import { clinicClient, type PimsWriteMode } from "@clinic/clinicClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

export const clinicKeys = {
  /** Prefix for invalidating every clinic query. */
  all: ["clinic"] as const,
  detail: (clinicUuid: string) => ["clinic", clinicUuid] as const,
};

/**
 * Current clinic identity (name, subdomain) from `GET /auth/clinic`.
 *
 * Non-suspense so it can feed app chrome (the document title) without blocking
 * the shell, and only runs once a clinic session exists.
 */
export const useClinic = () => {
  const clinicUuid = useSessionStore((s) => s.clinic?.clinicUuid ?? null);
  return useQuery({
    queryKey: clinicKeys.detail(clinicUuid ?? "anonymous"),
    queryFn: () => clinicClient.get(),
    enabled: clinicUuid !== null,
  });
};

/**
 * Set the clinic's `pims_write_mode` via `PATCH /auth/clinic`, then refetch the
 * clinic so the settings panel (and anything else on `useClinic`) reflects it.
 */
export const useUpdatePimsWriteMode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mode: PimsWriteMode) => clinicClient.setPimsWriteMode(mode),
    meta: { action: "change the PIMS write mode" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: clinicKeys.all }),
  });
};

/**
 * Set the `batch` flush cadence via `PATCH /auth/clinic/flush-interval`, then
 * refetch the clinic so the settings panel reflects it. Only meaningful in batch
 * mode (the endpoint 409s otherwise), so the UI gates the control on that.
 */
export const useUpdateFlushInterval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (minutes: number) => clinicClient.setFlushInterval(minutes),
    meta: { action: "change the flush interval" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: clinicKeys.all }),
  });
};

/**
 * Set the clinic's branding text (`address`) via `PATCH
 * /auth/clinic` (BE-1919), then refetch so the settings panel and PDF export
 * reflect it.
 */
export const useUpdateBranding = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (branding: ClinicBranding) =>
      clinicBrandingClient.setBranding(branding),
    meta: { action: "save branding" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: clinicKeys.all }),
  });
};

/**
 * Upload a clinic logo (presigned PUT → S3 → confirm, BE-1919), then refetch so
 * the new presigned `logoUrl` is picked up everywhere on `useClinic`.
 */
export const useUploadClinicLogo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => clinicBrandingClient.uploadLogo(file),
    meta: { action: "upload the logo" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: clinicKeys.all }),
  });
};

const BASE_TITLE = "Brian";

/** Reflects the clinic name in the document title, falling back to "Brian". */
export const useClinicDocumentTitle = () => {
  const { data } = useClinic();
  useEffect(() => {
    document.title = data?.name ? `${data.name} · ${BASE_TITLE}` : BASE_TITLE;
  }, [data?.name]);
};
